import React from 'react';
import ReactApexChart from 'react-apexcharts';
import { formatCustomDate, formatDateToLexicographicalOrder } from '../AnalyticsHelper';
import { DEFAULT_HEIGHT, PT2H } from '../AnalyticsConstants';
import moment from 'moment';
import CompatioLoader from 'Components/Common/components/CompatioLoader';

type ColumnAndLineChartProps = {
  data: { 
    name: string; 
    columnData: { x: string; y: number; }[]; 
    lineData: { x: string; y: number; }[]; 
  }[];
  xAxis?: string[];
  granularity?: string;
  timezone: string;
}

const ColumnAndLineChart = React.memo(({ data, xAxis, granularity, timezone }: ColumnAndLineChartProps) => {
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    setIsLoading(true);
    
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 100);

    return () => clearTimeout(timer);
  }, [data, xAxis]);

  const chartOptions: any = React.useMemo(() => ({
    chart: { 
      stacked: false,
      toolbar: { show: false },
    },
    xaxis: { 
      type: 'category', 
      categories: xAxis,
      labels: {
        show: true,
        formatter: function (value:any) {
          const date = new Date(value);
          if (granularity === PT2H) {
            return formatCustomDate(value);
          } else {
            return formatDateToLexicographicalOrder(date, timezone);
          }
        },
      }, 
    },
    colors: [],
    yaxis: [ 
      { title: { text: 'Column Series' } }, 
      { opposite: true, title: { text: 'Line Series' } } 
    ],
    stroke: { 
      width: 3, 
      curve: 'straight' 
    },
    plotOptions: { 
      bar: { columnWidth: '30%' }, 
      line: { strokeWidth: 3 } 
    },
    dataLabels: { 
      enabled: true, 
      formatter: function (val:any) { 
        return val + "%"; 
      }, 
      offsetY: -20, 
      style: { 
        fontSize: '8px', 
        colors: ["transparent"] 
      } 
    },
  }), [granularity, xAxis, timezone]);

  const series = React.useMemo(() => {
    return data.flatMap((entry, index) => [
      {
        name: `${entry.name} (Column)`,
        data: xAxis?.map((dateString) => {
          const categoryDate = moment.parseZone(dateString);
          const dataPoint = entry.columnData.find((point) => {
            const pointDate = moment.parseZone(point.x);
            return pointDate.isSame(categoryDate);
          });
          return dataPoint ? dataPoint.y : null;
        }),
        type: 'bar',
        yAxisIndex: 0,
      },
      {
        name: `${entry.name} (Line)`,
        data: xAxis?.map((dateString) => {
          const categoryDate = moment.parseZone(dateString);
          const dataPoint = entry.lineData.find((point) => {
            const pointDate = moment.parseZone(point.x);
            return pointDate.isSame(categoryDate);
          });
          return dataPoint ? dataPoint.y : null;
        }),
        type: 'line',
        yAxisIndex: 1,
      },
    ]);
  }, [data, xAxis]);

  if (isLoading) {
    return (
      <div className='tw-justify-center tw-flex tw-text-center tw-items-center' style={{ height: DEFAULT_HEIGHT }}>
        <CompatioLoader />
      </div>
    );
  }

  return (
    <div style={{ height: DEFAULT_HEIGHT }}>
      <ReactApexChart
        options={chartOptions}
        series={series as any}
        type="bar"
        height={DEFAULT_HEIGHT}
        width="100%"
      />
    </div>
  );
});

ColumnAndLineChart.displayName = 'ColumnAndLineChart';

export default ColumnAndLineChart;